import { useEffect, useMemo, useRef, useState } from 'react'
import { collectFlagAssets, preloadFlags } from '../utils/flag_utils'

// Whether an event's flags are in cache, so the rows can be held back and paint
// together with them instead of the flags arriving one by one afterwards.
//
// Only the first paint is gated. Polling hands back a fresh scopes object every
// few seconds, and re-gating on that would drop a leaderboard the user is
// already reading back to a spinner - so once the rows are out, they stay out
// and any newly arrived flag simply loads as it always did.
//
// Ready whenever there is nothing to wait for: no flag setting, no data yet, or
// no player carrying a flag.
const useFlagsReady = (scopes, displayedFlags, stateFlagUrls) => {
  const [ isReady, setIsReady ] = useState(false)
  const hasPaintedRef = useRef(false)

  const assets = useMemo(
    () => collectFlagAssets(scopes, displayedFlags, stateFlagUrls),
    [ scopes, displayedFlags, stateFlagUrls ],
  )

  // Identity, not the object: the flag set is what the wait depends on, and it
  // survives the poll that replaces scopes with an equal copy.
  const key = `${assets.countryCodes.join(',')}|${assets.stateFlagUrls.join(',')}`

  useEffect(() => {
    if (hasPaintedRef.current || !key.replace('|', '')) {
      setIsReady(true)
      return undefined
    }

    let cancelled = false
    setIsReady(false)

    const { promise, cancel } = preloadFlags(assets)

    promise.then(() => {
      if (!cancelled) {
        hasPaintedRef.current = true
        setIsReady(true)
      }
    })

    return () => {
      cancelled = true
      cancel()
    }
    // Keyed on the flag set alone: assets is derived from it, and depending on
    // that object would re-run this on every poll.
  }, [ key ])

  return isReady
}

export default useFlagsReady
